// js/GameUI.js

// Import dependencies
import GameCore from './GameCore.js';
import AccessibilityManager from './core/AccessibilityManager.js';
import AnimationManager from './AnimationManager.js'; // Import AnimationManager

/**
 * GameUI.js - UI rendering and management
 */

export default class GameUI { // Add export default
  /**
   * Create a new GameUI instance
   * @param {GameCore} gameCore - Instance of GameCore for game state access
   * @param {AccessibilityManager} accessibilityManager - For accessibility features
   */
  constructor(gameCore, accessibilityManager) {
    // Basic type checking for dependencies
    if (!(gameCore instanceof GameCore)) {
      throw new Error('GameUI requires a valid GameCore instance.');
    }
    if (!(accessibilityManager instanceof AccessibilityManager)) {
      throw new Error('GameUI requires a valid AccessibilityManager instance.');
    }
    this.gameCore = gameCore;
    this.accessibilityManager = accessibilityManager;
    /** @type {AnimationManager | null} */
    this.animationManager = null; // Will be set later

    // Cache DOM elements
    this.gridContainer = document.getElementById('grid-container');
    this.scoreDisplay = document.querySelector('.score-display'); // Cache score display
    this.swapCountElement = this.scoreDisplay?.querySelector('#swap-count');
    this.maxSwapsElement = this.scoreDisplay?.querySelector('#max-swaps');
    this.srAnnouncer = document.getElementById('sr-announcer');
    this.srStatus = document.getElementById('sr-status');
    this.hardModeCheckbox = document.getElementById('hard-mode-checkbox');
    this.hardModeContainer = document.getElementById('hard-mode-container');
    this.hintButton = document.getElementById('hint-button'); // Cache hint button
    this.resetButton = document.getElementById('reset-button'); // Still cache even if hidden
    this.swapCounterContainer = document.getElementById('swap-counter-container');
    this.swapCounterValue = document.getElementById('swap-counter-value');

    // Initialize properties for keyboard navigation
    this.keyboardFocusPosition = { row: 0, col: 0 };
    this.isKeyboardModeActive = false;

    // Message container management
    this.messageContainer = null;
    this.messageTimeout = null;
    this._retryButton = null;

    // Define handlers here for reliable removal
    this.gridKeydownHandler = this._onGridKeydown.bind(this);
    this.gridMousedownHandler = this._onGridMousedown.bind(this);
  }

  // --- Public Methods ---

  renderSkeleton(gridSize) {
    if (!this.gridContainer) return;
    this.gridContainer.replaceChildren();
    this.gridContainer.style.gridTemplateColumns = `repeat(${gridSize}, 1fr)`;
    this.gridContainer.setAttribute('aria-busy', 'true');

    const isMobile = window.matchMedia("(max-width: 600px)").matches;
    const currentGap = isMobile ? 5 : 8;
    this.gridContainer.style.gap = `${currentGap}px`;

    let baseTileSize;
    if (isMobile) {
      baseTileSize = gridSize <= 4 ? 70 : 60;
    } else {
      baseTileSize = gridSize <= 4 ? 105 : 90;
    }

    const maxContainerWidth = 500;
    let calculatedWidth = gridSize * baseTileSize + (gridSize - 1) * currentGap;
    let adjustedTileSize = baseTileSize;
    if (calculatedWidth > maxContainerWidth) {
      adjustedTileSize = Math.floor((maxContainerWidth - (gridSize - 1) * currentGap) / gridSize);
      calculatedWidth = gridSize * adjustedTileSize + (gridSize - 1) * currentGap;
    }

    this.gridContainer.style.width = `${calculatedWidth}px`;
    this.gridContainer.style.maxWidth = '100%';

    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        const tile = document.createElement('div');
        tile.classList.add('skeleton-tile', 'skeleton-pulse');
        tile.setAttribute('aria-label', `Loading, Row ${r + 1}, Column ${c + 1}`);
        tile.style.width = `${adjustedTileSize}px`;
        tile.style.height = `${adjustedTileSize}px`;
        this.gridContainer.appendChild(tile);
      }
    }

    this.announceToScreenReader('Loading puzzle...');
  }

  setGridBusy(busy) {
    if (!this.gridContainer) return;
    if (busy) {
      this.gridContainer.setAttribute('aria-busy', 'true');
    } else {
      this.gridContainer.removeAttribute('aria-busy');
    }
  }

  stopSkeletonPulse() {
    if (!this.gridContainer) return;
    this.gridContainer.querySelectorAll('.skeleton-pulse').forEach(el => el.classList.remove('skeleton-pulse'));
  }

  /**
   * Render the game grid based on GameCore state.
   */
  renderGrid() {
    if (!this.gridContainer) {
      console.error('Grid container not found!');
      return;
    }
    if (!this.gameCore || !this.gameCore.puzzle || !this.gameCore.puzzle.grid) {
      console.error('Game core, puzzle, or puzzle grid not initialized for rendering!');
      const errP = document.createElement('p');
      errP.textContent = 'Error loading puzzle grid.';
      this.gridContainer.replaceChildren(errP);
      return;
    }
    this.gridContainer.replaceChildren();
    this.gridContainer.removeAttribute('aria-busy');
    const gridSize = this.gameCore.gridSize;
    this.gridContainer.style.gridTemplateColumns = `repeat(${gridSize}, 1fr)`;

    // Determine the correct gap and base size based on screen size
    const isMobile = window.matchMedia("(max-width: 600px)").matches;
    const currentGap = isMobile ? 5 : 8;
    this.gridContainer.style.gap = `${currentGap}px`;

    // --- START MODIFICATION: Conditionally set base tile size ---
    let baseTileSize;
    if (isMobile) {
      // Mobile sizes (original values)
      baseTileSize = gridSize <= 4 ? 70 : 60;
    } else {
      // Desktop sizes (increased values)
      baseTileSize = gridSize <= 4 ? 105 : 90;
    }
    // --- END MODIFICATION ---

    const maxContainerWidth = 500; // Keep max width constraint

    // Calculate container width and tile size based on the selected base size and gap
    let calculatedWidth = gridSize * baseTileSize + (gridSize - 1) * currentGap;
    let adjustedTileSize = baseTileSize;

    // Adjust size if calculated width exceeds max container width (primarily for desktop)
    if (calculatedWidth > maxContainerWidth) {
        adjustedTileSize = Math.floor((maxContainerWidth - (gridSize - 1) * currentGap) / gridSize);
        calculatedWidth = gridSize * adjustedTileSize + (gridSize - 1) * currentGap;
    }

    // Set the calculated width for the container
    this.gridContainer.style.width = `${calculatedWidth}px`;
    this.gridContainer.style.maxWidth = '100%'; // Ensure it doesn't overflow viewport

    // Create row wrappers (for semantic structure, optional)
    const rowWrappers = [];
    for (let row = 0; row < gridSize; row++) {
      const rowElement = document.createElement('div');
      rowElement.className = 'grid-row-wrapper';
      rowElement.setAttribute('role', 'row');
      rowElement.style.display = 'contents'; // Use display: contents for grid layout
      this.gridContainer.appendChild(rowElement);
      rowWrappers.push(rowElement);
    }

    // Create and append tiles
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const tile = document.createElement('div');
        tile.className = 'tile';
        tile.dataset.row = row;
        tile.dataset.col = col;
        const letter = this.gameCore.puzzle.grid[row]?.[col];
        tile.textContent = letter ? letter.toUpperCase() : '';
        tile.tabIndex = -1; // Manage focus programmatically
        tile.setAttribute('role', 'gridcell');
        tile.setAttribute('aria-label', `${letter || 'empty'}, row ${row + 1}, column ${col + 1}`);

        // Apply the calculated adjusted tile size dynamically
        tile.style.width = `${adjustedTileSize}px`;
        tile.style.height = `${adjustedTileSize}px`;

        // Append to the correct row wrapper
        rowWrappers[row].appendChild(tile);
      }
    }

    // Update other UI elements and states
    this.updateValidation();
    this.updateControlStates();
    this.setupGridKeyboardNavigation();
    this.updateKeyboardFocusVisuals();
  }


  /**
   * Update the validation state (colors, ARIA labels) of all tiles based on GameCore state.
   */
  updateValidation() {
    const validationState = this.gameCore.getValidationState();

    if (!validationState) { return; }
    if (!this.gridContainer) return;

    const tiles = this.gridContainer.querySelectorAll('.tile');

    tiles.forEach((tile) => {
      tile.classList.remove('highlight-green', 'highlight-yellow');
      const currentLabelParts = tile.getAttribute('aria-label')?.split(',') || [];
      const baseLabel = currentLabelParts.slice(0, 3).join(',').trim();

      const row = parseInt(tile.dataset.row);
      const col = parseInt(tile.dataset.col);

      if (row >= 0 && row < this.gameCore.gridSize && col >= 0 && col < this.gameCore.gridSize) {
        const state = validationState[row]?.[col]; // Added safety check for row existence
        let stateDescription = 'movable';

        if (state === 4) {
          tile.classList.add('highlight-green');
          stateDescription = 'locked (correct position)';
        } else if (state >= 1) { // state can be 1, 2, or 3 for yellow
          tile.classList.add('highlight-yellow');
          stateDescription = 'correct letter, wrong position';
        }

        if (baseLabel) {
          tile.setAttribute('aria-label', `${baseLabel}, ${stateDescription}`);
        } else {
          const letter = this.gameCore.puzzle?.grid?.[row]?.[col] || 'empty';
          tile.setAttribute('aria-label', `${letter}, row ${row + 1}, column ${col + 1}, ${stateDescription}`);
        }
      } else {
        console.warn(`Tile with invalid coordinates found during validation: row ${row}, col ${col}`);
        if (baseLabel) tile.setAttribute('aria-label', `${baseLabel}, unknown state`);
      }
    });
  }

  /**
   * Update the swap count display visibility and content.
   */
  updateSwapCount() {
    if (!this.scoreDisplay || !this.swapCountElement || !this.maxSwapsElement || !this.gameCore?.puzzle) {
      console.warn("[updateSwapCount] Aborting: Required elements or puzzle data missing.");
      return;
    }

    const isHardModeActive = this.gameCore.isHardMode;
    const currentSwaps = this.gameCore.puzzle.swapCount;
    const maxSwaps = this.gameCore.MAX_SWAPS;

    // Toggle visibility using the 'hidden' class based on Hard Mode status
    // If Hard Mode is ACTIVE -> remove .hidden class
    // If Hard Mode is INACTIVE -> add .hidden class
    this.scoreDisplay.classList.toggle('hidden', !isHardModeActive);
    this.scoreDisplay.setAttribute('aria-hidden', String(!isHardModeActive));

    // Update content only if visible (i.e., Hard Mode is active)
    if (isHardModeActive) {
      this.swapCountElement.textContent = currentSwaps;
      this.maxSwapsElement.textContent = maxSwaps;

      // Swap warnings (keep this logic)
      if (currentSwaps === maxSwaps - 5 && maxSwaps > 5) {
        this.announceToScreenReader(`Warning: Only 5 swaps remaining!`, true);
      } else if (currentSwaps === maxSwaps - 3 && maxSwaps > 3) {
        this.announceToScreenReader(`Warning: Only 3 swaps remaining!`, true);
      } else if (currentSwaps === maxSwaps - 1 && maxSwaps > 1) {
        this.announceToScreenReader(`Warning: Last swap!`, true);
      }
    }

    // Update non-hard-mode swap counter
    if (this.swapCounterValue) {
      this.swapCounterValue.textContent = currentSwaps;
    }
  }


  /**
   * Show a temporary message to the user and announce it.
   */
  showMessage(message, duration = 3000) {
    if (this.messageTimeout) { clearTimeout(this.messageTimeout); this.messageTimeout = null; }
    if (!this.messageContainer) {
      this.messageContainer = document.createElement('div');
      this.messageContainer.id = 'message-container';
      Object.assign(this.messageContainer.style, {
          position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)', color: 'white', padding: '10px 20px',
          borderRadius: '5px', zIndex: '1000', textAlign: 'center',
          transition: 'opacity 0.3s ease', pointerEvents: 'none', opacity: '0',
          maxWidth: '90%',
      });
      document.body.appendChild(this.messageContainer);
    }
    this.messageContainer.textContent = message;
    requestAnimationFrame(() => {
        if (this.messageContainer) this.messageContainer.style.opacity = '1';
    });
    this.announceToScreenReader(message, true);
    if (duration > 0) {
      this.messageTimeout = setTimeout(() => {
          if (this.messageContainer) {
              this.messageContainer.style.opacity = '0';
          }
          this.messageTimeout = null;
      }, duration);
    }
  }

  clearMessage() {
    if (this.messageTimeout) { clearTimeout(this.messageTimeout); this.messageTimeout = null; }
    if (this.messageContainer) {
      this.messageContainer.style.opacity = '0';
    }
  }

  showRetryButton() {
    if (this._retryButton && this._retryButton.isConnected) return;
    if (this._retryButton) { this._retryButton.remove(); this._retryButton = null; }
    const btn = document.createElement('button');
    btn.textContent = 'Try Again';
    btn.classList.add('retry-button');
    btn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('puzzle-retry-requested'));
    });
    this._retryButton = btn;
    this.gridContainer.parentNode.insertBefore(btn, this.gridContainer.nextSibling);
  }

  hideRetryButton() {
    if (this._retryButton) {
      this._retryButton.remove();
      this._retryButton = null;
    }
  }

  showRedirectMessage(fromSize, toSize) {
    console.log(`[showRedirectMessage] fromSize=${fromSize} toSize=${toSize}`);
    this.clearMessage();
    this.hideRetryButton();
    this.stopSkeletonPulse();
    this.setGridBusy(false);
    const msg = `Today's ${fromSize}x${fromSize} puzzle isn't ready yet. Try ${toSize}x${toSize}!`;
    this.showMessage(msg, 0);
  }

  /**
   * Update UI elements related to game controls (Hard Mode checkbox, Hint/Reset buttons, Score display).
   * Includes hiding the hint button based on Hard Mode, Completion, or Hint Used.
   */
  updateControlStates() {
    if (!this.gameCore || !this.gameCore.puzzle) {
      console.warn("[updateControlStates] Skipping update: GameCore or puzzle not ready.");
      return;
    }

    const isHard = this.gameCore.isHardMode;
    const swaps = this.gameCore.puzzle.swapCount;
    // Check completion status directly from GameCore, which checks StorageManager
    const isCompleted = this.gameCore.hasCompletedTodaysPuzzle();
    const hintUsed = this.gameCore.hintUsed;

    // --- Update score display class/visibility ---
    this.scoreDisplay?.classList.toggle('hard-mode', isHard);
    this.updateSwapCount(); // This handles visibility based on isHard

    // --- Update Hint Button state and visibility ---
    if (this.hintButton) {
      // Determine if the hint button should be hidden/disabled
      const shouldHideHint = isCompleted || isHard;
      const isHintDisabled = shouldHideHint || hintUsed; // Disable if hidden OR hint used

      // Log current state for debugging
      // console.log(`[updateControlStates] Hint Button State: isCompleted=${isCompleted}, isHard=${isHard}, hintUsed=${hintUsed}, shouldHideHint=${shouldHideHint}, isHintDisabled=${isHintDisabled}`);

      // Toggle hidden class for visibility and fade-out animation
      this.hintButton.classList.toggle('hidden', shouldHideHint);
      this.hintButton.setAttribute('aria-hidden', String(shouldHideHint));

      // Toggle disabled state (for semantics and fallback)
      this.hintButton.disabled = isHintDisabled;
      this.hintButton.classList.toggle('disabled', isHintDisabled); // Keep disabled class for styling if needed

      // Update label based on the *reason* it's unavailable or the action
      let hintLabel = 'Request a hint (adds 3 swaps to score)';
      let hintText = 'Hint (+3 Swaps)';

      if (isCompleted) {
        hintLabel = 'Hint unavailable (Puzzle completed)';
        hintText = 'Hint'; // Text doesn't matter much when hidden
      } else if (isHard) {
        hintLabel = 'Hint unavailable (Hard Mode)';
        hintText = 'Hint'; // Text doesn't matter much when hidden
      } else if (hintUsed) {
        hintLabel = 'Hint already used';
        hintText = 'Hint (Used)';
      }

      this.hintButton.textContent = hintText;
      this.hintButton.setAttribute('aria-label', hintLabel);
      this.hintButton.setAttribute('aria-disabled', String(isHintDisabled));
    } else {
      console.warn("[updateControlStates] Hint button element not found.");
    }


    // --- Update Reset Button state and visibility (remains hidden by CSS) ---
    if (this.resetButton) {
      const resetDisabled = isCompleted;
      this.resetButton.disabled = resetDisabled;
      this.resetButton.classList.toggle('disabled', resetDisabled);
      // this.resetButton.classList.toggle('hidden', isCompleted); // CSS handles hiding
      this.resetButton.setAttribute('aria-hidden', 'true'); // Always hidden visually
      this.resetButton.setAttribute('aria-label', resetDisabled ? 'Reset unavailable (Puzzle completed)' : 'Reset Puzzle Progress');
    }

    // --- Update Hard Mode Checkbox state ---
    if (this.hardModeCheckbox) {
        this.hardModeCheckbox.checked = isHard;
        const hmCheckboxDisabled = swaps > 0 || isCompleted;
        this.hardModeCheckbox.disabled = hmCheckboxDisabled;
        this.hardModeCheckbox.setAttribute('aria-disabled', String(hmCheckboxDisabled));
    }

    // --- Update Hard Mode Container visibility ---
    if (this.hardModeContainer) {
        const hideContainer = swaps > 0 || isCompleted;
        this.hardModeContainer.classList.toggle('hidden', hideContainer);
        this.hardModeContainer.setAttribute('aria-hidden', String(hideContainer));
    }

    // --- Update Swap Counter (non-hard mode) ---
    if (this.swapCounterContainer) {
        const showCounter = swaps > 0 && !isHard;
        this.swapCounterContainer.classList.toggle('hidden', !showCounter);
        this.swapCounterContainer.setAttribute('aria-hidden', String(!showCounter));
        if (showCounter && this.swapCounterValue) {
            this.swapCounterValue.textContent = swaps;
        }
    }
  }

  /**
   * Updates the visual indicator (CSS class) for the keyboard focus position.
   */
  updateKeyboardFocusVisuals() {
    if (!this.gridContainer) return;
    const tiles = this.gridContainer.querySelectorAll('.tile');
    tiles.forEach((tile) => tile.classList.remove('keyboard-focus'));
    if (this.isKeyboardModeActive) {
      const { row, col } = this.keyboardFocusPosition;
      const focusedTile = this.gridContainer.querySelector(`.tile[data-row="${row}"][data-col="${col}"]`);
      if (focusedTile) {
          focusedTile.classList.add('keyboard-focus');
      }
    }
  }

  /**
   * Setup keyboard navigation listeners for the grid container.
   */
  setupGridKeyboardNavigation() {
    if (!this.gridContainer) return;
    this.gridContainer.removeEventListener('keydown', this.gridKeydownHandler);
    this.gridContainer.removeEventListener('mousedown', this.gridMousedownHandler);
    this.gridContainer.addEventListener('keydown', this.gridKeydownHandler);
    this.gridContainer.addEventListener('mousedown', this.gridMousedownHandler);
  }

  /**
   * Create a drag element for drag-and-drop.
   */
  createDragElement(tile, clientX, clientY, dragOffset, tileRect) {
    const existingClone = document.getElementById('drag-clone');
    if (existingClone) { existingClone.remove(); }
    const clone = tile.cloneNode(true);
    clone.id = 'drag-clone';
    Object.assign(clone.style, {
        position: 'fixed', zIndex: '1000', opacity: '0.8', pointerEvents: 'none',
        width: `${tileRect.width}px`, height: `${tileRect.height}px`,
        left: `${clientX - dragOffset.x}px`, top: `${clientY - dragOffset.y}px`,
        cursor: 'grabbing',
    });
    document.body.appendChild(clone);
    return clone;
  }

  /**
   * Find the tile element at a specific screen position.
   */
  findTileAtPosition(clientX, clientY) {
    const clone = document.getElementById('drag-clone');
    let originalPointerEvents = null;
    if (clone) {
        originalPointerEvents = clone.style.pointerEvents;
        clone.style.pointerEvents = 'none';
    }
    const element = document.elementFromPoint(clientX, clientY);
    if (clone && originalPointerEvents !== null) {
        clone.style.pointerEvents = originalPointerEvents;
    }
    if (element && element.classList.contains('tile') && this.gridContainer?.contains(element)) {
        return element;
    }
    return null;
  }

  /**
   * Update the drop target highlighting during drag.
   */
  updateDropTarget(clientX, clientY, draggedTile) {
    if (!this.gridContainer) return;
    const targetTile = this.findTileAtPosition(clientX, clientY);
    let currentTargetTile = null;
    if (targetTile && targetTile !== draggedTile && !targetTile.classList.contains('highlight-green')) {
        currentTargetTile = targetTile;
    }
    this.gridContainer.querySelectorAll('.tile').forEach(tile => {
        if (tile !== draggedTile) {
            tile.classList.toggle('drop-target', tile === currentTargetTile);
        } else {
            tile.classList.remove('drop-target');
        }
    });
  }

  /**
   * Highlight a specific tile temporarily to indicate a hint swap.
   */
  highlightHintTile(row, col) {
    if (!this.gridContainer) return;
    const tile = this.gridContainer.querySelector(`.tile[data-row="${row}"][data-col="${col}"]`);
    if (tile) {
      const originalTransition = tile.style.transition;
      tile.style.transition = 'background-color 0.3s ease, transform 0.3s ease';
      tile.style.backgroundColor = '#3498db'; // Hint color
      tile.style.transform = 'scale(1.1)';
      setTimeout(() => {
          if (tile) {
              tile.style.backgroundColor = ''; // Revert to default/validation color
              tile.style.transform = '';
              tile.style.transition = originalTransition; // Restore original transition
              // Re-apply validation after highlight fades to ensure correct color
              setTimeout(() => {
                  if (tile) this.updateValidation();
              }, 300); // Delay matches transition duration
          }
      }, 1000); // Highlight duration
    }
  }


  /**
   * Set keyboard navigation position, ensuring it stays within grid bounds.
   */
  setKeyboardFocusPosition(row, col) {
    const gridSize = this.gameCore?.gridSize || 0;
    if (gridSize === 0) return; // Don't update if grid size is unknown

    const validRow = Math.max(0, Math.min(gridSize - 1, row));
    const validCol = Math.max(0, Math.min(gridSize - 1, col));
    if (this.keyboardFocusPosition.row !== validRow || this.keyboardFocusPosition.col !== validCol) {
        this.keyboardFocusPosition = { row: validRow, col: validCol };
        this.updateKeyboardFocusVisuals();
    }
  }

  /**
   * Set keyboard mode active state and update visuals. Announce state change.
   */
  setKeyboardModeActive(isActive) {
    if (this.isKeyboardModeActive !== isActive) {
        this.isKeyboardModeActive = isActive;
        this.updateKeyboardFocusVisuals();
        if (isActive) {
            this.announceToScreenReader('Keyboard navigation active.', false);
            // Ensure the visually focused tile actually has DOM focus
            const { row, col } = this.keyboardFocusPosition;
            const focusedTile = this.gridContainer?.querySelector(`.tile[data-row="${row}"][data-col="${col}"]`);
            focusedTile?.focus();
        }
    }
  }

  /**
   * Get keyboard mode active state.
   */
  isKeyboardMode() {
    return this.isKeyboardModeActive;
  }

  /**
   * Announce a message to screen readers using AccessibilityManager or fallback.
   */
  announceToScreenReader(message, isAlert = false) {
    if (this.accessibilityManager && typeof this.accessibilityManager.announce === 'function') {
        this.accessibilityManager.announce(message, isAlert);
    } else {
        const target = isAlert ? this.srAnnouncer : this.srStatus;
        if (target) {
            target.textContent = message;
            // Clear after a delay to prevent repeated announcements of the same message
            setTimeout(() => {
                if (target.textContent === message) { // Only clear if it hasn't been overwritten
                    target.textContent = '';
                }
            }, 7000); // Increased delay slightly
        } else {
            console.warn("Screen reader announcement failed: Target element not found.");
        }
    }
  }


  // --- Private handlers for listeners --- //

  /**
   * Handles keydown on the grid container, primarily to prevent scrolling with arrow keys.
   */
  _onGridKeydown(event) {
      if (event.key.startsWith('Arrow')) {
          event.preventDefault(); // Prevent page scroll
          // Navigation logic is handled in InputHandler.handleGlobalKeydown
      }
  }

  /**
   * Handles mousedown on the grid container to disable keyboard mode.
   */
  _onGridMousedown() {
      if (this.isKeyboardModeActive) {
          this.setKeyboardModeActive(false);
      }
  }

  // Add reference to AnimationManager if needed for drag element creation etc.
  setAnimationManager(animationManager) {
      if (animationManager instanceof AnimationManager) {
          this.animationManager = animationManager;
      } else {
          console.error("Invalid AnimationManager instance provided to GameUI.");
      }
  }

} // End of GameUI class
// Removed window export
